import React from 'react';
import {
  SparkFormDatePicker,
  SparkFormFileUploader,
  SparkFormTimePicker,
  SparkEnumButton,
  SparkRadioGroup,
  SparkSlider,
  SparkFormPopupTextField,
  SparkFormCheckBoxField,
} from 'sharedComponents/SparkForm';
import { UI_COMPONENT } from 'constants/uiComponents';
import { DropdownOption } from 'types/common/DropdownOption';
import { useUiComponents } from 'hooks/SparkFormBuilder';
import { SliderOptions } from 'types/common';
import { useField, useFormikContext } from 'formik';
import SparkDynamicDropdownInputField from './SparkDynamicDropdownInputField';
import SparkDynamicTextInputField from './SparkDynamicTextInputField';
import SparkDynamicTextAreaInputField from './SparkDynamicTextAreaInputField';

type DynamicUIComponentFieldControllerProps = {
  name: string;
  label: string | null | undefined;
  helpText: string | null | undefined;
  subHeaderText: string | null | undefined;
  placeholder: string | null | undefined;
  uiComponentID: number | undefined;
  options?: DropdownOption[];
  popOverContent?: React.ReactNode | null;
  sliderOptions: SliderOptions;
  defaultValue?: string;
  isFieldMapped?: boolean;
  isFieldMappable?: boolean;
  forceFieldMappable?: boolean;
  onFieldSwitch?: (mapped: boolean) => void;
};

const DynamicUIComponentFieldController = ({
  name,
  helpText = '- -',
  label: headerText = '',
  placeholder: placeholderText = '',
  subHeaderText = '',
  uiComponentID,
  options = [],
  popOverContent,
  sliderOptions,
  defaultValue,
  isFieldMapped = false,
  isFieldMappable = false,
  forceFieldMappable = false,
  onFieldSwitch,
}: DynamicUIComponentFieldControllerProps) => {
  const { uiComponentsIdByNameMap } = useUiComponents();
  const [field, _, helper] = useField<string>(name);
  const { values } = useFormikContext<Record<string, string>>();
  const applyKnowledge = values['applyKnowledge'];
  React.useEffect(() => {
    if (!defaultValue || field.value || !applyKnowledge) {
      return;
    }

    helper.setValue(defaultValue);
    //need to fix this after
    //eslint-disable-next-line
  }, [applyKnowledge, defaultValue, helper]);

  switch (uiComponentID + '') {
    case uiComponentsIdByNameMap?.[UI_COMPONENT.DATE]:
      return (
        <SparkFormDatePicker
          key={name}
          name={name}
          size={12}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          dateFormat='YYYY/MM/DD'
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.MULTIPLE_CHOICE]:
      return (
        <SparkRadioGroup
          key={name}
          name={name}
          options={options}
          size={12}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.CHECKBOXES]:
      return (
        <SparkFormCheckBoxField
          key={name}
          name={name}
          size={12}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
        />
        // <SparkEnumCheckboxGroup
        //   key={name}
        //   name={name}
        //   options={options}
        //   helpText={helpText || ''}
        //   subHeaderText={subHeaderText || ''}
        // />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.BUTTONS]:
      return (
        <SparkEnumButton
          key={name}
          name={name}
          options={options}
          size={12}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.TIME]:
      return (
        <SparkFormTimePicker
          key={name}
          name={name}
          size={12}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.FILE_UPLOAD]:
      return (
        <SparkFormFileUploader
          key={name}
          name={name}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          size={12}
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.SLIDER]:
      return (
        <SparkSlider
          key={name + Date.now()}
          name={name}
          label={headerText || '- -'}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          size={12}
          {...sliderOptions}
        />
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.TEXT_AREA]:
      if (!popOverContent) {
        return (
          <SparkDynamicTextAreaInputField
            label={headerText || '- -'}
            name={name}
            key={name}
            placeholder={placeholderText || ''}
            helpText={helpText || ''}
            subHeaderText={subHeaderText || ''}
            isFieldMappable={forceFieldMappable}
          />
        );
      }
      return (
        <SparkFormPopupTextField
          key={name}
          name={name}
          label={headerText || '- -'}
          placeholder={placeholderText || ''}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          size={12}
          multiline={true}
          rows={3}
          defaultValue='asdku'
        >
          {popOverContent}
        </SparkFormPopupTextField>
      );
    case uiComponentsIdByNameMap?.[UI_COMPONENT.DROPDOWN]:
      return (
        <SparkDynamicDropdownInputField
          label={headerText || '- -'}
          name={name}
          key={name}
          placeholder={placeholderText || ''}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          onFieldSwitch={onFieldSwitch}
          isFieldMapped={isFieldMapped}
          isFieldMappable={isFieldMappable}
          options={options}
        />
      );

    case uiComponentsIdByNameMap?.[UI_COMPONENT.TEXTFIELD]:
      if (!popOverContent) {
        return (
          <SparkDynamicTextInputField
            label={headerText || '- -'}
            name={name}
            key={name}
            placeholder={placeholderText || ''}
            helpText={helpText || ''}
            subHeaderText={subHeaderText || ''}
            isFieldMappable={forceFieldMappable}
          />
        );
      }
      return (
        <SparkFormPopupTextField
          key={name}
          name={name}
          label={headerText || '- -'}
          placeholder={placeholderText || ''}
          helpText={helpText || ''}
          subHeaderText={subHeaderText || ''}
          size={12}
        >
          {popOverContent}
        </SparkFormPopupTextField>
      );
    default:
      return <></>;
  }
};

export default DynamicUIComponentFieldController;
